import { all, fork, put, select, takeLatest } from 'redux-saga/effects';
import { PlaceArTreasureSTep } from '../../../constant/placeArTreasure';
import { goToPage, ROUTE_PLACE_AR_TREASURE_DETAIL } from '../routing';
import {
  ACTION_REQUEST_UPDATE_TREASURE_SUBMIT,
  ACTION_UPDATE_LOCAL_TREASURE_DISPLAY,
  ACTION_UPDATE_LOCAL_TREASURE_LOCATION,
  ACTION_UPDATE_LOCAL_TREASURE_PRIZE,
  ACTION_UPDATE_LOCAL_TREASURE_PRIZE_TYPE,
  ACTION_UPDATE_LOCAL_TREASURE_AVAILABILITY,
  ACTION_RESET_LOCAL_TREASURE,
  ACTION_REQUEST_FETCH_AR_TEMPLATES,
  ACTION_REQUEST_GENERATE_3D_MODEL,
  ACTION_REQUEST_CONVERT_GLB_TO_USDZ,
  fetchListCountry,
  fetchTreasureById,
  fetchArTemplatesAPI,
  generate3DModelAPI,
  convertGlbToUsdzAPI,
  REQUEST_EDIT_TREASURE,
  REQUEST_EDIT_TREASURE_FAILURE,
  REQUEST_EDIT_TREASURE_SUCCESS,
  REQUEST_UPDATE_TREASURE_FAILURE,
  REQUEST_UPDATE_TREASURE_SUCCESS,
  REQUEST_FETCH_AR_TEMPLATES_SUCCESS,
  REQUEST_FETCH_AR_TEMPLATES_FAILURE,
  REQUEST_GENERATE_3D_MODEL_SUCCESS,
  REQUEST_GENERATE_3D_MODEL_FAILURE,
  REQUEST_CONVERT_GLB_TO_USDZ_SUCCESS,
  REQUEST_CONVERT_GLB_TO_USDZ_FAILURE,
  REQUEST_FETCH_LIST_WINNERS_SUCCESS,
  REQUEST_FETCH_LIST_WINNERS_FAILURE,
  REQUEST_EDIT_WINNERS_SUCCESS,
  REQUEST_EDIT_WINNERS_FAILURE,
  submitTreasureAPI,
} from './index';
import { showToastAlert } from '../app';
import { fetchUserInfo } from '../auth';
import { toast } from 'react-toastify';
import {
  ACTION_HANDLE_ADD_SHARE_LINK,
  ACTION_REQUEST_GET_SHARE_LINK,
  actionResetBodyShareLink,
  actionResetShareLink,
  actionUpdateBodyShareLink,
  createShareLinkAPI,
  getShareLinkAPI,
  REQUEST_CREATE_SHARE_LINK_DATA_FAILURE,
  REQUEST_GET_SHARE_LINK_DATA_FAILURE,
  REQUEST_GET_SHARE_LINK_DATA_SUCCESS,
} from '../shareLink';
import { getErrorMessage } from '../../../utils/error';
import { handleShareLink } from '../../../utils/shareSocials';
import {
  REQUEST_UPLOAD_FILE_FAILURE,
  REQUEST_UPLOAD_FILE_SUCCESS,
  uploadFileAPI,
} from '../uploadFile';

function* loadRouteData() {
  // Reset local treasure data before starting
  yield put({ type: ACTION_RESET_LOCAL_TREASURE });

  // Initialize by fetching countries list
  yield put(fetchListCountry());

  // Navigate to first step (display)
  yield put(
    goToPage(
      ROUTE_PLACE_AR_TREASURE_DETAIL,
      { id: 'new' },
      {
        meta: {
          query: { step: PlaceArTreasureSTep.DISPLAY },
        },
      }
    )
  );
}

function* fetchTreasureDetail() {
  const { location } = yield select();
  const treasureId = location?.payload?.id;

  yield put(fetchTreasureById(treasureId));
}

function* updateTreasure() {
  const { location } = yield select();
  const treasureId = location?.payload?.id;

  // Only fetch treasure data if editing an existing treasure (not 'new')
  if (treasureId && treasureId !== 'new') {
    yield put(fetchTreasureById(treasureId));
  }
  yield put(fetchListCountry());

  // Handle local updates (just navigate to next step, no API calls)
  yield takeLatest(
    [
      ACTION_UPDATE_LOCAL_TREASURE_DISPLAY,
      ACTION_UPDATE_LOCAL_TREASURE_LOCATION,
      ACTION_UPDATE_LOCAL_TREASURE_PRIZE,
      ACTION_UPDATE_LOCAL_TREASURE_PRIZE_TYPE,
      ACTION_UPDATE_LOCAL_TREASURE_AVAILABILITY,
    ],
    function* () {
      const { location, placeArTreasure } = yield select();
      const { localTreasureData } = placeArTreasure;
      const treasureId = location?.payload?.id;
      const step = location?.query?.step;
      const treasureSteps = Object.values(PlaceArTreasureSTep);
      const currentStepIndex = treasureSteps.indexOf(step);
      let nextStepIndex = 0;
      if (currentStepIndex >= 0) {
        nextStepIndex = currentStepIndex + 1;
      }

      // Special case: Skip Prize Type step if rewardType is NOT selected (null or undefined)
      // If rewardType is selected, go to Prize Type to enter details
      if (
        step === PlaceArTreasureSTep.PRIZE &&
        treasureSteps[nextStepIndex] === PlaceArTreasureSTep.PRIZE_TYPE &&
        (localTreasureData?.treasurePrize?.rewardType === null ||
          localTreasureData?.treasurePrize?.rewardType === undefined)
      ) {
        nextStepIndex = nextStepIndex + 1; // Skip to Availability step
      }

      yield put(
        goToPage(
          ROUTE_PLACE_AR_TREASURE_DETAIL,
          {
            id: treasureId || 'new',
          },
          {
            meta: {
              query: {
                step: treasureSteps[nextStepIndex],
              },
            },
          }
        )
      );
    }
  );

  // Handle the final submit action - this calls the API
  yield takeLatest([ACTION_REQUEST_UPDATE_TREASURE_SUBMIT], function* (action) {
    const { placeArTreasure } = yield select();
    const { localTreasureData } = placeArTreasure;

    // Prepare payload by combining all local data
    const payload = {
      // Display data
      clueName: localTreasureData.treasureDisplay?.clueName,
      description: localTreasureData.treasureDisplay?.description,
      arDisplay: localTreasureData.treasureDisplay?.arDisplay,
      arPreviewUrl: localTreasureData.treasureDisplay?.arPreviewUrl,
      arOwnLogoUrl: localTreasureData.treasureDisplay?.arPreviewUrl,
      arMessage: localTreasureData.treasureDisplay?.arMessage,
      defaultAr: localTreasureData.treasureDisplay?.defaultAr,

      // Location data
      address: localTreasureData.treasureLocation?.address,
      locationName: localTreasureData.treasureLocation?.locationName,
      latitude: localTreasureData.treasureLocation?.latitude,
      longitude: localTreasureData.treasureLocation?.longitude,

      // Prize data
      rewardType: localTreasureData.treasurePrize?.rewardType,
      prize: localTreasureData.treasurePrize?.prize,

      // Prize type data (varies based on rewardType)
      magicWords: localTreasureData.treasurePrizeType?.magicWords,
      promoCode: localTreasureData.treasurePrizeType?.promoCode,
      onlineStorePromoCode: localTreasureData.treasurePrizeType?.onlineStorePromoCode,
      productImageUrl: localTreasureData.treasurePrizeType?.productImageUrl,
      productDescription: localTreasureData.treasurePrizeType?.productDescription,
      countries: localTreasureData.treasurePrizeType?.countries,

      // Availability data
      startDate: localTreasureData.treasureAvailability?.startDate,
      stopDate: localTreasureData.treasureAvailability?.stopDate,
      maxClaims: localTreasureData.treasureAvailability?.maxClaims,

      // Clue from action payload
      riddle: action.payload?.riddle,
      arTemplateId: localTreasureData.treasureDisplay?.arTemplateId,
      modelUrls: localTreasureData.treasureDisplay?.modelUrls,
    };

    yield put(submitTreasureAPI(payload));
  });

  // Handle successful submission
  yield takeLatest([REQUEST_UPDATE_TREASURE_SUCCESS], function* () {
    const { placeArTreasure } = yield select();
    const { treasure } = placeArTreasure;

    yield put(fetchUserInfo());
    yield put(
      goToPage(
        ROUTE_PLACE_AR_TREASURE_DETAIL,
        {
          id: treasure?.id,
        },
        {
          meta: {
            query: {
              step: PlaceArTreasureSTep.SUBMITTED,
            },
          },
        }
      )
    );
  });

  yield takeLatest([REQUEST_UPDATE_TREASURE_FAILURE], function* (action) {
    const errorMessage =
      action?.payload?.data?.message?.[0]?.error || action?.payload?.data?.message;
    yield put(showToastAlert(errorMessage || `Failed to Submit Treasure!`, 'error'));
  });
}

function* editPlaceArTreasure() {
  yield takeLatest([REQUEST_EDIT_TREASURE], function* () {
    toast.info('Updating Treasure...');
    yield;
  });

  yield takeLatest([REQUEST_EDIT_TREASURE_SUCCESS], function* () {
    yield toast.success('Update Treasure Successfully!');
  });

  yield takeLatest([REQUEST_EDIT_TREASURE_FAILURE], function* (action) {
    const errorMessage =
      action?.payload?.data?.message?.[0]?.error || action?.payload?.data?.message;
    toast.error(errorMessage);
    yield;
  });
}

function* loadPlaceArLandingData() {
  yield all([put(actionResetBodyShareLink()), put(actionResetShareLink())]);
}

function* handleAddShareLink() {
  yield takeLatest([ACTION_HANDLE_ADD_SHARE_LINK], function* (action) {
    const { payload } = action;
    const { file } = payload;
    yield put(uploadFileAPI(file));
  });

  yield takeLatest([REQUEST_UPLOAD_FILE_SUCCESS], function* (action) {
    const { shareLinkStore } = yield select();
    const { shareLinkBody } = shareLinkStore;
    const image = action?.payload?.data?.url;
    yield put(
      actionUpdateBodyShareLink({
        ...shareLinkBody,
        image,
      })
    );
    yield put(
      createShareLinkAPI({
        ...shareLinkBody,
        image,
      })
    );
  });

  yield takeLatest([ACTION_REQUEST_GET_SHARE_LINK], function* (action) {
    const { payload } = action;
    yield put(getShareLinkAPI(payload));
  });

  yield takeLatest([REQUEST_GET_SHARE_LINK_DATA_SUCCESS], function* (action) {
    const newShareLink = action.payload.data;
    const type = action.payload?.type;
    const { shareLinkStore } = yield select();
    const { shareLinkBody } = shareLinkStore;
    
    // Only handle share link if type is provided (for social sharing)
    if (type || shareLinkBody.type) {
      yield handleShareLink(type || shareLinkBody.type, newShareLink);
    }
  });

  yield takeLatest(
    [
      REQUEST_UPLOAD_FILE_FAILURE,
      REQUEST_CREATE_SHARE_LINK_DATA_FAILURE,
      REQUEST_GET_SHARE_LINK_DATA_FAILURE,
    ],
    function* (action) {
      const errorMessage = getErrorMessage(action.payload) || `Failed to create share link!`;
      yield put(showToastAlert(errorMessage, 'error'));
    }
  );
}

function* handleFetchArTemplates() {
  yield takeLatest([ACTION_REQUEST_FETCH_AR_TEMPLATES], function* (action) {
    const { payload } = action;
    yield put(fetchArTemplatesAPI(payload));
  });

  yield takeLatest([REQUEST_FETCH_AR_TEMPLATES_SUCCESS], function* () {
    // yield put(showToastAlert('AR templates fetched successfully!', 'success'));
    yield;
  });

  yield takeLatest([REQUEST_FETCH_AR_TEMPLATES_FAILURE], function* (action) {
    const errorMessage = getErrorMessage(action.payload) || `Failed to fetch AR templates!`;
    yield put(showToastAlert(errorMessage, 'error'));
  });
}

function* handleGenerate3DModel() {
  yield takeLatest([ACTION_REQUEST_GENERATE_3D_MODEL], function* (action) {
    const { payload } = action;
    yield put(generate3DModelAPI(payload));
  });

  yield takeLatest([REQUEST_GENERATE_3D_MODEL_SUCCESS], function* () {
    yield put(showToastAlert('3D model generated successfully!', 'success'));
  });

  yield takeLatest([REQUEST_GENERATE_3D_MODEL_FAILURE], function* (action) {
    const errorMessage = getErrorMessage(action.payload) || `Failed to generate 3D model!`;
    yield put(showToastAlert(errorMessage, 'error'));
  });
}

function* handleConvertGlbToUsdz() {
  yield takeLatest([ACTION_REQUEST_CONVERT_GLB_TO_USDZ], function* (action) {
    const { payload } = action;
    yield put(convertGlbToUsdzAPI(payload));
  });

  yield takeLatest([REQUEST_CONVERT_GLB_TO_USDZ_SUCCESS], function* () {
    yield put(showToastAlert('GLB converted to USDZ successfully!', 'success'));
  });

  yield takeLatest([REQUEST_CONVERT_GLB_TO_USDZ_FAILURE], function* (action) {
    const errorMessage = getErrorMessage(action.payload) || `Failed to convert GLB to USDZ!`;
    yield put(showToastAlert(errorMessage, 'error'));
  });
}

function* handleUploadFile() {
  yield takeLatest([REQUEST_UPLOAD_FILE_FAILURE], function* (action) {
    const errorMessage = getErrorMessage(action.payload) || `Failed to upload file!`;
    yield put(showToastAlert(errorMessage, 'error'));
  });
}

function* handleFetchWinners() {
  yield takeLatest([REQUEST_FETCH_LIST_WINNERS_SUCCESS], function* () {
    // Successfully fetched winners list
    yield;
  });

  yield takeLatest([REQUEST_FETCH_LIST_WINNERS_FAILURE], function* (action) {
    const errorMessage = getErrorMessage(action.payload) || `Failed to fetch winners list!`;
    yield put(showToastAlert(errorMessage, 'error'));
  });
}

function* handleWinnerDetails() {
  yield takeLatest([REQUEST_EDIT_WINNERS_SUCCESS], function* () {
    // No toast notification for winner details fetch
    yield;
  });

  yield takeLatest([REQUEST_EDIT_WINNERS_FAILURE], function* (action) {
    const errorMessage = getErrorMessage(action.payload) || `Failed to fetch winner details!`;
    yield put(showToastAlert(errorMessage, 'error'));
  });
}

export function* loadPlaceArTreasurePage() {
  yield all([fork(loadRouteData)]);
}
export function* loadingPlaceArTreasureLanding() {
  yield all([fork(loadPlaceArLandingData), fork(handleAddShareLink)]);
}

export function* loadPlaceArTreasureDetailPage() {
  yield all([
    fork(updateTreasure),
    fork(handleFetchArTemplates),
    fork(handleGenerate3DModel),
    fork(handleConvertGlbToUsdz),
    fork(handleUploadFile),
    fork(handleAddShareLink),
  ]);
}

export function* loadPlaceArTreasureEditPage() {
  yield all([fork(fetchTreasureDetail)]);
}

export function* editPlaceArTreasurePage() {
  yield all([
    fork(editPlaceArTreasure),
    fork(handleFetchWinners),
    fork(handleWinnerDetails)
  ]);
}
